import { useCallback, useMemo } from "react";
import { useNavigate, type NavigateOptions } from "@tanstack/react-router";
import { useWorkspace } from "#app/lib/data/workspace-context";

/**
 * A route built at run time — under the active workspace's slug, or from a
 * stored preference — that the router's generated route types cannot see.
 * This is the ONE place the app asserts such a path; every `Navigate` and
 * `navigate()` that composes a path goes through it, so a wrong path fails
 * here and nowhere else.
 */
export function routePath(path: string): "/" {
  return path as unknown as "/";
}

/**
 * `routePath` plus search: `to` is a pathname ONLY for the router, so a query
 * string in the path is dropped; search always rides its own option.
 */
export function routeOptions(
  path: string,
  search?: Record<string, unknown>,
  extra?: { replace?: boolean },
): NavigateOptions {
  return {
    to: routePath(path),
    ...(search ? { search } : {}),
    ...extra,
  } as NavigateOptions;
}

/**
 * Workspace-aware navigation that works from anywhere — screens, sheets,
 * portals. Builds fully-qualified hrefs under the active workspace so
 * tab/route switches resolve correctly regardless of navigation context.
 *
 * Usage:
 * ```ts
 * const { push, replace } = useAppNavigate();
 * push("/map");                        // navigates to map tab
 * push("/settings/teams");             // navigates to teams settings
 * replace("/settings/teams");          // replaces current route (back nav)
 * ```
 */
export function useAppNavigate() {
  const navigate = useNavigate();
  const { activeWorkspace } = useWorkspace();
  const wp = activeWorkspace?.urlPath ?? "";

  // Split an optional `?query` off the route and turn it into a real `search`
  // object. TanStack Router treats `to` as a pathname ONLY — a query string
  // embedded in `to` is never parsed into typed search params, so navigating to
  // `/map?focusLat=…` (especially map→map) silently drops the params and does
  // nothing. Search must be passed via the `search` option.
  const buildOptions = useCallback(
    (route: string) => {
      const queryIndex = route.indexOf("?");
      const path = queryIndex === -1 ? route : route.slice(0, queryIndex);
      const to = path === "/" ? `/${wp}` : `/${wp}${path}`;
      if (queryIndex === -1) return routeOptions(to);
      const search = Object.fromEntries(
        new URLSearchParams(route.slice(queryIndex + 1)),
      );
      return routeOptions(to, search);
    },
    [wp],
  );

  return useMemo(
    () => ({
      push: (route: string) => navigate(buildOptions(route)),
      replace: (route: string) =>
        navigate({ ...buildOptions(route), replace: true }),
    }),
    [navigate, buildOptions],
  );
}
